import { useState } from 'react'

import { useTailgateGrid } from '../../hooks/useTailgateGrid'
import { useIncidentSearch } from '../hooks/useIncidentSearch'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { Box, Flex, HStack, Tbody, Td, Text, Tr } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  Flex as DarkFlex,
  HStack as DarkHStack,
  Radio as DarkRadio,
  Table as DarkTable,
  TableBody as DarkTableBody,
  TableCell as DarkTableCell,
  TableHeader as DarkTableHeader,
  TableHeaderCell as DarkTableHeaderCell,
  TablePagination as DarkTablePagination,
  TableRow as DarkTableRow,
  Text as DarkText,
  TextInput as DarkTextInput,
  SearchIcon,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import {
  Button,
  SearchInput,
  Table,
  TableFooter,
  TableHeader,
  TableSpinner,
} from '@/components/ui'
import { IncidentType } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { IncidentOptionI } from '../../types/types'
import { formatEnumLabel } from '../../utils/formatEnumLabel'
import {
  TYPE_FILTER_OPTIONS,
  incidentTableColumns,
} from '../constants/incidentTable'

interface IncidentSearchPanelIProps {
  colorScheme?: ColorScheme
  selectedIncident: IncidentOptionI | null
  setSelectedIncident: (option: IncidentOptionI | null) => void
  /**
   * When provided, renders a "Use ${displayId}" button above the filters so
   * the user can switch back to the preset incident without re-selecting it
   * from the table.
   */
  presetIncident?: IncidentOptionI | null
  onUsePreset?: () => void
}

export const IncidentSearchPanel = ({
  colorScheme,
  selectedIncident,
  setSelectedIncident,
  presetIncident,
  onUsePreset,
}: IncidentSearchPanelIProps) => {
  const tokenPathPrefix = colorScheme ? tokenPrefix(colorScheme) : ''
  const secondaryTextColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}text.secondary`)
    : 'gray.500'
  const { associateDeviceValues } = useTailgateGrid()
  const [typeFilter, setTypeFilter] = useState<IncidentType | null>(null)
  const {
    incidents,
    isLoading,
    searchTerm,
    setSearchTerm,
    totalCount,
    pageIndex,
    pageSize,
    pageCount,
    goToPage,
    nextPage,
    previousPage,
  } = useIncidentSearch(associateDeviceValues?.doorId, typeFilter)

  const table = useReactTable({
    data: incidents,
    columns: incidentTableColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: { selectedId: selectedIncident?.id },
  })

  return (
    <>
      {presetIncident &&
        onUsePreset &&
        (colorScheme ? (
          <DarkBox mb='12px'>
            <DarkButton
              colorScheme={colorScheme}
              onClick={onUsePreset}
              size='sm'
              variant='secondary'
            >
              Use {presetIncident.incidentId}
            </DarkButton>
          </DarkBox>
        ) : (
          <Box mb={3}>
            <Button
              _hover={{ bg: 'purple.100' }}
              bg='purple.50'
              border='1px solid'
              borderColor='purple.200'
              color='purple.700'
              onClick={onUsePreset}
              size='sm'
              variant='ghost'
            >
              Use {presetIncident.incidentId}
            </Button>
          </Box>
        ))}
      {colorScheme ? (
        <DarkHStack gap='8px'>
          {TYPE_FILTER_OPTIONS.map((option) => (
            <DarkButton
              colorScheme={colorScheme}
              key={option.label}
              onClick={() => {
                setTypeFilter(option.value)
                goToPage(0)
              }}
              size='sm'
              variant={typeFilter === option.value ? 'primary' : 'tertiary'}
            >
              {option.label}
            </DarkButton>
          ))}
        </DarkHStack>
      ) : (
        <HStack spacing={2}>
          {TYPE_FILTER_OPTIONS.map((option) => (
            <Button
              key={option.label}
              onClick={() => {
                setTypeFilter(option.value)
                goToPage(0)
              }}
              size='sm'
              variant={typeFilter === option.value ? 'primary' : 'outline'}
            >
              {option.label}
            </Button>
          ))}
        </HStack>
      )}
      {colorScheme ? (
        <DarkBox mt='12px'>
          <DarkTextInput
            colorScheme={colorScheme}
            data-testid='GridTestingZone:incidentSearch'
            height='42px'
            inputProps={{
              'aria-label': 'Search by Incident ID',
              autoCapitalize: 'off',
              autoComplete: 'off',
              autoCorrect: 'off',
              type: 'search',
            }}
            leftIcon={<SearchIcon colorScheme={colorScheme} size={14} />}
            onValueChange={setSearchTerm}
            placeholder='Search by Incident ID'
            value={searchTerm}
            width='100%'
          />
        </DarkBox>
      ) : (
        <Box mt={3}>
          <SearchInput
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder='Search by Incident ID'
            value={searchTerm}
          />
        </Box>
      )}
      {colorScheme ? (
        <DarkBox mt='16px'>
          {isLoading || incidents.length > 0 ? (
            <DarkTable colorScheme={colorScheme} density='compact'>
              <DarkTableHeader>
                <DarkTableHeaderCell width='40px' />
                <DarkTableHeaderCell width='100px'>
                  Incident ID
                </DarkTableHeaderCell>
                <DarkTableHeaderCell width='100px'>Type</DarkTableHeaderCell>
                <DarkTableHeaderCell flexGrow={1} minWidth='140px'>
                  Created
                </DarkTableHeaderCell>
                <DarkTableHeaderCell width='90px'>Status</DarkTableHeaderCell>
              </DarkTableHeader>
              <DarkTableBody isLoading={isLoading} skeletonRowCount={4}>
                {incidents.map((incident, index) => (
                  <DarkTableRow
                    isAlternate={index % 2 === 1}
                    isSelected={incident.id === selectedIncident?.id}
                    key={incident.id}
                    onClick={() => setSelectedIncident(incident)}
                  >
                    <DarkTableCell align='center' width='40px'>
                      <DarkRadio
                        aria-label={`Select incident ${incident.incidentId}`}
                        colorScheme={colorScheme}
                        isChecked={incident.id === selectedIncident?.id}
                        label=''
                        onChange={() => setSelectedIncident(incident)}
                        value={incident.id}
                      />
                    </DarkTableCell>
                    <DarkTableCell width='100px'>
                      {incident.incidentId}
                    </DarkTableCell>
                    <DarkTableCell width='100px'>
                      {formatEnumLabel(incident.type)}
                    </DarkTableCell>
                    <DarkTableCell flexGrow={1} minWidth='140px'>
                      {formatDateTime(incident.createdAt)}
                    </DarkTableCell>
                    <DarkTableCell width='90px'>
                      {formatEnumLabel(incident.status)}
                    </DarkTableCell>
                  </DarkTableRow>
                ))}
              </DarkTableBody>
              {pageCount >= 1 && (
                <DarkTablePagination
                  onPageChange={goToPage}
                  pageCount={pageCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  total={totalCount}
                />
              )}
            </DarkTable>
          ) : (
            <DarkFlex
              alignItems='center'
              height='200px'
              justifyContent='center'
            >
              <DarkText color={secondaryTextColor} fontSize='14px'>
                No incidents with video clips found for this door
              </DarkText>
            </DarkFlex>
          )}
        </DarkBox>
      ) : (
        <Box mt={4}>
          {isLoading ? (
            <TableSpinner height='200px' />
          ) : incidents.length === 0 ? (
            <Flex align='center' h='200px' justify='center'>
              <Text color={secondaryTextColor} fontSize='sm'>
                No incidents with video clips found for this door
              </Text>
            </Flex>
          ) : (
            <Table
              tableContainerStyles={{
                overflowX: 'hidden',
                borderRadius: 'xl',
              }}
              tableStyles={{ sx: { tableLayout: 'fixed' } }}
            >
              <TableHeader px={2} table={table} />
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr
                    _hover={{ bgColor: 'gray.100' }}
                    bg={
                      row.original.id === selectedIncident?.id
                        ? 'blue.50'
                        : undefined
                    }
                    cursor='pointer'
                    data-testid={`row-${row.original.incidentId}`}
                    h='32px'
                    key={row.id}
                    onClick={() => setSelectedIncident(row.original)}
                    transition='ease-in-out'
                    transitionDuration='300ms'
                    transitionProperty='all'
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        color={secondaryTextColor}
                        fontSize='11px'
                        fontWeight={500}
                        key={cell.id}
                        px={2}
                        py={1}
                        w={cell.column.getSize()}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
                {/* Pad with blank rows to maintain constant table height when fewer than 10 results */}
                {Array.from({
                  length: pageSize - table.getRowModel().rows.length,
                }).map((_, i) => (
                  <Tr borderBottom='none' h='32px' key={`blank-${i}`}>
                    <Td
                      borderBottom='none'
                      colSpan={incidentTableColumns.length}
                    />
                  </Tr>
                ))}
              </Tbody>
              {pageCount >= 1 && (
                <TableFooter
                  onGoToPage={goToPage}
                  onNextPage={nextPage}
                  onPreviousPage={previousPage}
                  pageCount={pageCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  showBorder={false}
                  total={totalCount}
                />
              )}
            </Table>
          )}
        </Box>
      )}
    </>
  )
}
